/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useViewportSize } from "../../utils/utils";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { courses } from "./courses";
import ConfettiCannons from "../ConfettiCannons";
import { Button } from "../Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";
import { enterpriseAPI } from "../..";
import localforage from "localforage";
import "./Tutorial.css";
import { useUser } from "../UserContext";

interface TutorialProps {
  courseSelected?: SelectedCourse;
  onNext: () => void;
  onClose: () => void;
  onStart?: () => void;
}

export interface SelectedCourse {
  id: string;
  currentChapter: number;
}

interface Course {
  id: string;
}

export interface CourseSummary extends Course {
  title: string;
  description: string;
  totalChapters: number;
  preview: React.ReactElement;
  skipInDev?: boolean;
}

export interface CourseContent extends Course {
  chapters: Chapter[];
}

interface Chapter {
  Amary: Amary;
  Mask: MaskProps;
  ContentBox: ContentBox;
}

interface AmaryDetails {
  position: { x: string; y: string };
  extraCSS?: React.CSSProperties;
  speed: number;
  delayedCSS?: { [key: number]: React.CSSProperties };
  delay?: number;
}

export interface Amary extends AmaryDetails {
  id: string;
}

type MaskType = "circle" | "rectangle" | "horizontal-pill" | undefined;

export interface ContentBox {
  position: { x: string; y: string };
  extraCSS?: React.CSSProperties;
  animationSpeed: number;
  delayedCSS?: { [key: number]: React.CSSProperties };
  text: string;
  speechSpeed: number;
  speechFast: boolean;
  delay?: number;
  onReady: () => void;
  continueTextPos: "bottom" | "top";
  triggerNextType: "anywhere" | "mask-only";
}

export interface MaskProps {
  type: MaskType;
  speed: number;
  elementId: string;
  widthDiff?: number;
  heightDiff?: number;
  posXDiff?: number;
  posYDiff?: number;
  /**
   * If set, the tutorial only advances after the user clicks the spotlight
   * AND the URL ends up matching this. Pass a string for exact match, or a
   * predicate function for prefix/regex/whatever matching (useful when a
   * route loader redirects, e.g. `/anuncios` → `/anuncios/radio`). Guards
   * against clicks that don't actually trigger the expected navigation.
   */
  expectedRoute?: string | ((pathname: string) => boolean);
  /**
   * Element IDs whose bounding boxes should remain clickable even while the
   * tutorial overlay is active (e.g. the sidebar nav, so the user can still
   * jump between sections without being trapped in the current step). The
   * overlay carves rectangular pass-through holes for each of these.
   */
  unblockedElementIds?: string[];
  /**
   * Keys that satisfy this chapter in addition to clicking the spotlight.
   *
   * Needed whenever the spotlighted control has a keyboard equivalent that
   * REMOVES it from the DOM: the mask-only listener only advances on a click
   * whose target is inside `elementId`, so once that element unmounts the
   * chapter can never be satisfied — and the blocker rectangles keep covering
   * the viewport. See the keydown effect in Tutorial.tsx for the measured case.
   */
  advanceOnKeys?: string[];
  /**
   * When true, the spotlight rect is computed as the union of the visible
   * direct children's rects instead of the container's own bounding box.
   * Useful for layout containers (filter pill bars, chip groups) that have
   * extra horizontal padding/space the user shouldn't see highlighted, and
   * which adapt size with breakpoints — the spotlight stays "tight" around
   * the actual content even at different viewport widths.
   */
  fitToVisibleChildren?: boolean;
}

function calculateElementPosition(elementId: string): { x: number; y: number } {
  const elem = document.getElementById(elementId);
  if (!elem) return { x: 0, y: 0 };

  const rect = elem.getBoundingClientRect();
  const x = rect.left + rect.width / 2 + window.scrollX;
  const y = rect.top + rect.height / 2 + window.scrollY;

  return { x, y };
}

type Rect = { x: number; y: number; w: number; h: number };

/**
 * Returns the rectangles that remain after subtracting `hole` from `r`.
 * Up to four sub-rectangles (top/bottom/left/right slices). Used to build
 * click-blocking divs that leave the spotlight + any pass-through elements
 * clickable.
 */
function subtractRect(r: Rect, hole: Rect): Rect[] {
  const rRight = r.x + r.w;
  const rBottom = r.y + r.h;
  const holeRight = hole.x + hole.w;
  const holeBottom = hole.y + hole.h;

  // No overlap → original rectangle survives intact.
  if (hole.x >= rRight || holeRight <= r.x || hole.y >= rBottom || holeBottom <= r.y) {
    return [r];
  }

  const out: Rect[] = [];
  if (hole.y > r.y) {
    out.push({ x: r.x, y: r.y, w: r.w, h: hole.y - r.y });
  }
  if (holeBottom < rBottom) {
    out.push({ x: r.x, y: holeBottom, w: r.w, h: rBottom - holeBottom });
  }
  const midTop = Math.max(r.y, hole.y);
  const midBottom = Math.min(rBottom, holeBottom);
  const midH = midBottom - midTop;
  if (midH > 0) {
    if (hole.x > r.x) {
      out.push({ x: r.x, y: midTop, w: hole.x - r.x, h: midH });
    }
    if (holeRight < rRight) {
      out.push({ x: holeRight, y: midTop, w: rRight - holeRight, h: midH });
    }
  }
  return out;
}

function calculateElementSize(elementId: string): { width: number; height: number } {
  const elem = document.getElementById(elementId);
  if (!elem) return { width: 0, height: 0 };

  const rect = elem.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

/**
 * Computes a tight rect that wraps only the visible direct children of the
 * given element. Useful when a layout container has horizontal padding,
 * gaps, or hidden items that shouldn't be highlighted by the spotlight.
 * Falls back to the element's own bounding rect if no visible children
 * are found.
 */
function calculateVisibleChildrenRect(
  elementId: string,
): { x: number; y: number; width: number; height: number } | null {
  const elem = document.getElementById(elementId);
  if (!elem) return null;

  const visibleRects: DOMRect[] = [];
  for (const child of Array.from(elem.children)) {
    const style = window.getComputedStyle(child);
    if (style.display === "none" || style.visibility === "hidden") continue;
    const r = child.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    visibleRects.push(r);
  }

  if (visibleRects.length === 0) {
    const fallback = elem.getBoundingClientRect();
    return {
      x: fallback.left,
      y: fallback.top,
      width: fallback.width,
      height: fallback.height,
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of visibleRects) {
    if (r.left < minX) minX = r.left;
    if (r.top < minY) minY = r.top;
    if (r.right > maxX) maxX = r.right;
    if (r.bottom > maxY) maxY = r.bottom;
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

const AmaryContent: React.FC<Amary> = ({ id, position, extraCSS, speed, delayedCSS, delay }) => {
  const [posX, setPosX] = React.useState<string>("0");
  const [posY, setPosY] = React.useState<string>("0");
  const [extraCSSState, setExtraCSSState] = React.useState<React.CSSProperties>({});

  function amarySrc(id: string): string {
    return `/assets/amary/${id}.png`;
  }

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPosX(position.x);
      setPosY(position.y);
      setExtraCSSState(extraCSS || {});
    }, delay ?? 0);

    return () => clearTimeout(timeoutId);
  }, [position.x, position.y, extraCSS, delay]);

  React.useEffect(() => {
    if (!delayedCSS) return;

    const timeouts: NodeJS.Timeout[] = [];

    Object.entries(delayedCSS).forEach(([delayKey, styleObj]) => {
      const baseDelay = Number(delayKey);
      if (Number.isNaN(baseDelay)) return;

      const timeoutId = setTimeout(() => {
        const elem = document.getElementById("tutorialAmary");
        if (elem) {
          Object.assign(elem.style, styleObj);
        }
      }, baseDelay);

      timeouts.push(timeoutId);
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [delayedCSS, delay]);

  return (
    <img
      src={amarySrc(id)}
      id="tutorialAmary"
      alt="Amary"
      style={{
        position: "absolute",
        left: `${posX}`,
        top: `${posY}`,
        transition: `all ${speed}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
        ...extraCSSState,
      }}
    />
  );
};

const BoxContent: React.FC<ContentBox> = React.memo((props) => {
  const [posX, setPosX] = React.useState<string>("0");
  const [posY, setPosY] = React.useState<string>("0");
  const [currentText, setCurrentText] = React.useState<string>("");
  const [reachEndChapter, setReachEndChapter] = React.useState<boolean>(false);
  const [extraCSSState, setExtraCSSState] = React.useState<React.CSSProperties>({});

  const speedSpeech = React.useRef<number>(
    props.speechFast ? props.speechSpeed / 4 : props.speechSpeed,
  );
  const speechNodeJS = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    speedSpeech.current = props.speechFast ? props.speechSpeed / 4 : props.speechSpeed;
  }, [props.speechFast, props.speechSpeed]);

  React.useEffect(() => {
    if (speechNodeJS.current) {
      clearTimeout(speechNodeJS.current);
      speechNodeJS.current = null;
    }

    setPosX(props.position.x);
    setPosY(props.position.y);
    setExtraCSSState(props.extraCSS || {});
    setCurrentText("");
    setReachEndChapter(false);

    const text = props.text;
    let index = 0;
    let cancelled = false;

    const typeNextChar = () => {
      if (cancelled) return;
      if (index < text.length) {
        const ch = text.charAt(index);
        setCurrentText((prev) => prev + ch);
        index += 1;
        speechNodeJS.current = setTimeout(typeNextChar, speedSpeech.current);
      } else {
        speechNodeJS.current = null;
        props.onReady();
        setReachEndChapter(true);
      }
    };

    const startTimeout = setTimeout(() => {
      if (!cancelled) typeNextChar();
    }, props.delay ?? 0);

    return () => {
      cancelled = true;
      clearTimeout(startTimeout);
      if (speechNodeJS.current) {
        clearTimeout(speechNodeJS.current);
        speechNodeJS.current = null;
      }
    };
  }, [props.text, props.position.x, props.position.y, props.extraCSS, props.delay, props.onReady]);

  React.useEffect(() => {
    if (!props.delayedCSS) return;

    const timeouts: NodeJS.Timeout[] = [];

    Object.entries(props.delayedCSS).forEach(([delayKey, styleObj]) => {
      const baseDelay = Number(delayKey);
      if (Number.isNaN(baseDelay)) return;

      const timeoutId = setTimeout(() => {
        const elem = document.getElementById("tutorialContentBox");
        if (elem) {
          Object.assign(elem.style, styleObj);
        }
      }, baseDelay);

      timeouts.push(timeoutId);
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [props.delayedCSS, props.delay]);

  return (
    <div
      id="tutorialContentBox"
      style={{
        left: `${posX}`,
        top: `${posY}`,
        transition: `all ${props.animationSpeed}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
        ...extraCSSState,
      }}
    >
      <div className="tutorialContentBoxContinueTextContainer">
        <span
          className={`tutorialContentBoxContinueText ${
            props.continueTextPos === "top"
              ? "tutorialContentBoxContinueTextTop"
              : "tutorialContentBoxContinueTextBottom"
          }`}
          style={reachEndChapter ? { opacity: 1 } : {}}
        >
          Clique para continuar...
        </span>
        <span className="tutorialContentBoxText">{currentText}</span>
      </div>
    </div>
  );
});

/**
 * Amary's guided tour: a spotlight mask over the page plus the character and
 * her speech box.
 *
 * `courseSelected` is not free-form — it is a cursor INTO `courses` (see
 * ./courses.tsx), so `id` has to be one of that record's keys and
 * `currentChapter` an index that exists in it. The render body dereferences
 * `courses[id].content` directly and is meant to: an id no course answers to is
 * a caller bug, and failing loudly at the source beats a silent empty overlay.
 * A synthesized `{ id: "id-0001", currentChapter: 42 }` satisfies the TYPE and
 * satisfies nothing else, so the story states the real cursor: chapter 0 of
 * `welcome`, the tour a new collaborator actually sees first.
 *
 * @story args { courseSelected: { id: "welcome", currentChapter: 0 } }
 */
export const Tutorial: React.FC<TutorialProps> = (props) => {
  const { concludedTutorials, setConcludedTutorials } = useUser();
  const { width, height } = useViewportSize();
  // Tutorials use pixel-precise spotlight masks tied to desktop layout,
  // so they don't translate to phone viewports. Suppress entirely below
  // 768 — users get the same access via the support inbox cards.
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [reachEndTutorial, setReachEndTutorial] = React.useState<boolean>(false);
  const [speechFast, setSpeechFast] = React.useState<boolean>(false);
  const [mask, setMask] = React.useState<MaskProps>({
    type: undefined,
    speed: 500,
    elementId: "",
    widthDiff: 0,
    heightDiff: 0,
    posXDiff: 0,
    posYDiff: 0,
  });
  const lastCenterRef = React.useRef<{ cx: number; cy: number } | null>(null);
  const clickHandlerRef = React.useRef<((e: Event) => void) | null>(null);
  // Tracks whether the current chapter has finished typing. While false the
  // spotlight div itself stays pointer-events:auto and absorbs clicks —
  // so users can't trigger the underlying React handler (bid, navigate,
  // close modal) before reading the explanation. Once true, the hole
  // becomes propagable: clicks fall through to the spotlighted element
  // and the tutorial listener advances.
  const [chapterReady, setChapterReady] = React.useState<boolean>(false);
  const chapterReadyRef = React.useRef<boolean>(false);
  // Snapshot of chapterReady at the moment of mousedown — used by the
  // anywhere-mode listener (which has nothing to "block" visually).
  const wasReadyAtMouseDownRef = React.useRef<boolean>(false);

  async function setTutorialAsConcluded() {
    if (!props.courseSelected) return;
    if (concludedTutorials.includes(props.courseSelected.id)) return;

    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      await enterpriseAPI.patch(`/set-tutorials-status/${props.courseSelected.id}`, null, {
        headers,
      });
      setConcludedTutorials((prev: string[]) => [...prev, props.courseSelected!.id]);
    } catch (error) {
      console.error("Error setting tutorial as concluded:", error);
    }
  }

  const onReadyContentBoxNext = React.useCallback(() => {
    if (!props.courseSelected) return;
    const nextChapter = props.courseSelected.currentChapter + 1 || 0;
    const courseContent = courses[props.courseSelected.id].content;

    if (nextChapter < courseContent.chapters.length) {
      props.onNext();
      // Always sync mask state to the next chapter immediately. The hole
      // shrinks/grows + the overlay lightens/darkens via CSS transitions
      // on width/height/box-shadow — no opacity fade-out hack needed.
      const nextMask = courseContent.chapters[nextChapter].Mask;
      setMask({
        type: nextMask.type,
        speed: nextMask.speed,
        elementId: nextMask.elementId,
        heightDiff: nextMask.heightDiff,
        widthDiff: nextMask.widthDiff,
        posXDiff: nextMask.posXDiff,
        posYDiff: nextMask.posYDiff,
        expectedRoute: nextMask.expectedRoute,
        unblockedElementIds: nextMask.unblockedElementIds,
        fitToVisibleChildren: nextMask.fitToVisibleChildren,
      });
    } else {
      setTutorialAsConcluded();
      setReachEndTutorial(true);
    }
  }, [props.onNext, props.courseSelected]);

  const handleBoxReady = React.useCallback(() => {
    if (!props.courseSelected) return;

    const ch =
      courses[props.courseSelected.id].content.chapters[props.courseSelected.currentChapter];

    if (ch) {
      setMask({
        type: ch.Mask.type,
        speed: ch.Mask.speed,
        elementId: ch.Mask.elementId,
        heightDiff: ch.Mask.heightDiff,
        widthDiff: ch.Mask.widthDiff,
        posXDiff: ch.Mask.posXDiff,
        posYDiff: ch.Mask.posYDiff,
        expectedRoute: ch.Mask.expectedRoute,
        unblockedElementIds: ch.Mask.unblockedElementIds,
        fitToVisibleChildren: ch.Mask.fitToVisibleChildren,
      });
    }

    ch.ContentBox.onReady();
    chapterReadyRef.current = true;
    setChapterReady(true);

    // Mask-only chapters get their listener attached up front via a
    // dedicated useEffect (see below). "Anywhere"-trigger chapters wait
    // for the text to finish before listening — otherwise a click during
    // the speech would skip the chapter without the user reading it.
    if (ch.Mask.type === undefined || ch.ContentBox.triggerNextType !== "mask-only") {
      const advance = () => {
        // Same guard as the mask-only handler: ignore clicks whose
        // mousedown happened before the chapter was ready (i.e. while the
        // user was holding to speed up the speech).
        if (!wasReadyAtMouseDownRef.current) {
          document.addEventListener("click", advance, { once: true });
          return;
        }
        onReadyContentBoxNext();
      };
      document.addEventListener("click", advance, { once: true });
    }
  }, [onReadyContentBoxNext]);

  // Attach the mask-only click listener as soon as the chapter is selected
  // (BEFORE the speech finishes typing). Without this, clicks during the
  // Amary monologue land on the React onClick of the spotlighted element
  // (e.g. submit-bid, cancel) but never reach the tutorial — so the user
  // performs the action AND the tutorial gets stuck waiting on a follow-up
  // click that may never come (target may have been unmounted).
  React.useEffect(() => {
    // Each chapter starts "not ready" — handleBoxReady flips it once the
    // speech finishes typing. The click handlers ignore clicks whose
    // mousedown happened before this transition, so a long-press to speed
    // up the speech doesn't auto-advance on release.
    chapterReadyRef.current = false;
    setChapterReady(false);

    if (!props.courseSelected) return;
    const ch =
      courses[props.courseSelected.id]?.content?.chapters?.[props.courseSelected.currentChapter];
    if (!ch) return;
    if (ch.Mask.type === undefined || ch.ContentBox.triggerNextType !== "mask-only") return;

    const expectedRoute = ch.Mask.expectedRoute;
    const elementId = ch.Mask.elementId;

    const onSpotlightClick = (e: Event) => {
      const me = e as MouseEvent;

      // Drop clicks whose mousedown started before the chapter was ready
      // (i.e. the user was holding to speed up the speech). They release →
      // a click event fires, but the user didn't actually intend to advance.
      if (!wasReadyAtMouseDownRef.current) return;

      if (!expectedRoute) {
        const elem = document.getElementById(elementId);
        // The spotlighted control can vanish while its chapter waits (the bid
        // button turns into the wait notice once the bid lands, Esc closes the
        // post). The hole is then 0x0 and the blocker covers the viewport, so
        // any click must continue the tour or it stays locked until a reload.
        if (!elem) {
          document.removeEventListener("click", onSpotlightClick, true);
          clickHandlerRef.current = null;
          onReadyContentBoxNext();
          return;
        }
        // Inspect e.target during capture phase — before React's onClick
        // handler runs and potentially unmounts the clicked element.
        if (!elem.contains(me.target as Node)) return;
        document.removeEventListener("click", onSpotlightClick, true);
        clickHandlerRef.current = null;
        onReadyContentBoxNext();
        return;
      }

      // Detach IMMEDIATELY so a rapid second click doesn't queue a second
      // timeout (which would cause a double-advance once both timeouts
      // verify the same pathname). If the route check fails afterwards we
      // re-attach so the user can retry.
      document.removeEventListener("click", onSpotlightClick, true);
      clickHandlerRef.current = null;

      window.setTimeout(() => {
        const pathname = window.location.pathname;
        const matches =
          typeof expectedRoute === "function"
            ? expectedRoute(pathname)
            : pathname === expectedRoute;
        if (matches) {
          onReadyContentBoxNext();
          return;
        }
        // Wrong target — give the user another shot.
        document.addEventListener("click", onSpotlightClick, true);
        clickHandlerRef.current = onSpotlightClick;
      }, 80);
    };

    clickHandlerRef.current = onSpotlightClick;
    // Capture phase: runs BEFORE React's bubble-phase onClick handlers, so
    // we can read e.target before any handler unmounts it, AND we know the
    // tutorial captured the click before any side-effect (navigation, bid
    // submission, modal close) takes hold.
    document.addEventListener("click", onSpotlightClick, true);

    return () => {
      document.removeEventListener("click", onSpotlightClick, true);
      if (clickHandlerRef.current === onSpotlightClick) {
        clickHandlerRef.current = null;
      }
    };
  }, [props.courseSelected, onReadyContentBoxNext]);

  // Keyboard exits declared by the chapter (`Mask.advanceOnKeys`).
  //
  // Measured on the negotiation tour: the chapter that says «Clica em 'fechar'
  // (ou aperta Esc)» spotlights `cancelPostDetailsButton<pos>` in mask-only
  // mode. Escape is handled by PostDetails itself (negotiate.tsx: `document`
  // keydown → `dismiss()`), so the modal — and the spotlighted button with it —
  // unmounts. `onSpotlightClick` then bails on every click, because
  // `document.getElementById(elementId)` is null; meanwhile the hole collapses
  // to 0×0, so `blockerRects` is the entire viewport, and on /negociar the
  // `unblockedElementIds` sidebar ids resolve to nothing (that route mounts
  // <Root> without a `sidebar` prop). The tour was unrecoverable short of a
  // page reload.
  //
  // Deliberately NOT gated on `chapterReady`: a keypress cannot be swallowed by
  // the overlay, so the modal closes whether or not the speech finished typing.
  // Ignoring an early Escape would restore the very lock this removes.
  React.useEffect(() => {
    if (!props.courseSelected) return;
    const ch =
      courses[props.courseSelected.id]?.content?.chapters?.[props.courseSelected.currentChapter];
    const keys = ch?.Mask.advanceOnKeys;
    if (!keys || keys.length === 0) return;

    const onAdvanceKey = (e: KeyboardEvent) => {
      if (!keys.includes(e.key)) return;
      // Detach before advancing so a key held down (auto-repeat) cannot fire
      // the chapter transition twice.
      document.removeEventListener("keydown", onAdvanceKey);
      onReadyContentBoxNext();
    };

    document.addEventListener("keydown", onAdvanceKey);
    return () => document.removeEventListener("keydown", onAdvanceKey);
  }, [props.courseSelected, onReadyContentBoxNext]);

  React.useEffect(() => {
    const onHoldClickDown = () => {
      setSpeechFast(true);
      // Snapshot whether the chapter is ready AT the moment of mousedown.
      // The click handlers consult this flag to decide whether to advance.
      wasReadyAtMouseDownRef.current = chapterReadyRef.current;
    };

    const onHoldClickUp = () => {
      setSpeechFast(false);
    };

    window.addEventListener("mousedown", onHoldClickDown);
    window.addEventListener("mouseup", onHoldClickUp);
    window.addEventListener("touchstart", onHoldClickDown);
    window.addEventListener("touchend", onHoldClickUp);

    return () => {
      window.removeEventListener("mousedown", onHoldClickDown);
      window.removeEventListener("mouseup", onHoldClickUp);
      window.removeEventListener("touchstart", onHoldClickDown);
      window.removeEventListener("touchend", onHoldClickUp);
    };
  }, [props]);

  // Force a re-render every 250ms while a tutorial is active, so the hole
  // tracks the highlighted element as the page mounts/scrolls and any
  // pass-through (unblocked) rectangles also stay in sync with their
  // elements. Without this, calculateElementSize/Position runs once with
  // stale (often 0,0) values when the target hasn't mounted yet.
  const [, refreshTick] = React.useReducer((n: number) => n + 1, 0);
  const tutorialActive = !!props.courseSelected;
  React.useEffect(() => {
    if (!tutorialActive) return;
    const id = window.setInterval(refreshTick, 250);
    return () => window.clearInterval(id);
  }, [tutorialActive]);

  // Reset the "Parabéns" end-screen flag whenever a new course starts —
  // otherwise the previous tutorial's end state leaks into the next one
  // because <Tutorial> never unmounts; it just renders an empty fragment
  // when no course is selected.
  const courseId = props.courseSelected?.id;
  React.useEffect(() => {
    if (courseId) setReachEndTutorial(false);
  }, [courseId]);

  if (!props.courseSelected) return <></>;
  // Mobile suppresses tutorials entirely — see hook declaration above.
  if (isMobile) return <></>;

  const chapter =
    courses[props.courseSelected.id].content.chapters[props.courseSelected.currentChapter];
  if (!chapter) {
    console.error(
      `Chapter ${props.courseSelected.currentChapter} not found for course ID "${props.courseSelected.id}".`,
    );
    return <></>;
  }

  const maskType = mask.type;
  let holeW = 0;
  let holeH = 0;
  let holeCenterX = 0;
  let holeCenterY = 0;
  if (mask.fitToVisibleChildren) {
    const tight = calculateVisibleChildrenRect(mask.elementId);
    if (tight) {
      holeW = tight.width;
      holeH = tight.height;
      holeCenterX = tight.x + tight.width / 2;
      holeCenterY = tight.y + tight.height / 2;
    }
  } else {
    const size = calculateElementSize(mask.elementId);
    const pos = calculateElementPosition(mask.elementId);
    holeW = size.width;
    holeH = size.height;
    holeCenterX = pos.x;
    holeCenterY = pos.y;
  }

  if (mask.widthDiff) {
    holeW += mask.widthDiff;
  }
  if (mask.heightDiff) {
    holeH += mask.heightDiff;
  }
  if (mask.posXDiff) {
    holeCenterX += mask.posXDiff;
  }
  if (mask.posYDiff) {
    holeCenterY += mask.posYDiff;
  }

  if (maskType === "horizontal-pill") {
    // Add exactly one height worth of width so the pill's rounded caps
    // (border-radius = h/2) sit cleanly on each side of the content,
    // forming a true stadium shape: rectangle + two semicircles flush
    // with the content edges. Larger multipliers make the pill drift
    // visibly past the highlighted area.
    holeW = holeW + holeH;
  }

  const holeX = holeCenterX - holeW / 2;
  const holeY = holeCenterY - holeH / 2;

  if (reachEndTutorial) {
    return (
      <div id="tutorialContainer" className="light">
        <div id="endTutorialContent">
          <ConfettiCannons fire={true} />
          <h2>Parabéns por concluir o tutorial!</h2>
          <Button
            class="endTutorialButton"
            callback={props.onClose}
            icon={<FontAwesomeIcon className="endTutorialButtonIcon" icon={faTrophy} />}
            iconSlot="end"
          >
            Concluir
          </Button>
        </div>
      </div>
    );
  }

  // The spotlight div is ALWAYS rendered. When there's no highlight the
  // hole shrinks to width=0/height=0 (still centered on the last known
  // position) and the surrounding overlay relaxes to a very light tint.
  // When there IS a highlight the hole grows to the target shape and the
  // overlay deepens. CSS transitions on width/height/top/left/box-shadow
  // produce a smooth grow/shrink + dim/lighten animation.
  const hasHole = maskType !== undefined && holeW > 0 && holeH > 0;
  if (hasHole) {
    lastCenterRef.current = { cx: holeCenterX, cy: holeCenterY };
  }
  const renderCx = hasHole ? holeCenterX : (lastCenterRef.current?.cx ?? width / 2);
  const renderCy = hasHole ? holeCenterY : (lastCenterRef.current?.cy ?? height / 2);
  const renderW = hasHole ? holeW : 0;
  const renderH = hasHole ? holeH : 0;
  const renderTop = renderCy - renderH / 2;
  const renderLeft = renderCx - renderW / 2;

  const holeRadius = (() => {
    switch (maskType) {
      case "circle":
        return "50%";
      case "horizontal-pill":
        return `${Math.min(renderW, renderH) / 2}px`;
      case "rectangle":
      default:
        return "0";
    }
  })();

  // Overlay is much darker when a spotlight is on; backs off to a softer
  // tint when no element is highlighted (still enough contrast for Amary
  // and the speech box to stand out from the page background). Halo only
  // glows around an active spotlight.
  const overlayAlpha = hasHole ? 0.85 : 0.35;
  const haloAlpha = hasHole ? 0.32 : 0;
  const animSpeed = mask.speed ?? 500;
  const transition = [
    `top ${animSpeed}ms ease`,
    `left ${animSpeed}ms ease`,
    `width ${animSpeed}ms ease`,
    `height ${animSpeed}ms ease`,
    `border-radius ${animSpeed}ms ease`,
    `box-shadow ${animSpeed}ms ease`,
  ].join(", ");

  // Build the click-blocking layer as a list of rectangles that fill the
  // viewport minus the spotlight and minus any always-clickable elements.
  let blockerRects: Rect[] = [{ x: 0, y: 0, w: width, h: height }];
  if (hasHole) {
    blockerRects = blockerRects.flatMap((r) =>
      subtractRect(r, { x: holeX, y: holeY, w: holeW, h: holeH }),
    );
  }
  for (const id of mask.unblockedElementIds ?? []) {
    const elem = document.getElementById(id);
    if (!elem) continue;
    const er = elem.getBoundingClientRect();
    const hole: Rect = { x: er.left, y: er.top, w: er.width, h: er.height };
    blockerRects = blockerRects.flatMap((r) => subtractRect(r, hole));
  }

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: renderTop,
          left: renderLeft,
          width: renderW,
          height: renderH,
          borderRadius: holeRadius,
          boxShadow: [
            `0 0 32px 12px rgba(255, 255, 255, ${haloAlpha})`,
            `0 0 0 9999px rgba(0, 0, 0, ${overlayAlpha})`,
          ].join(", "),
          // Block clicks on the spotlighted element until the speech ends,
          // so the user can't trigger the React onClick (bid, navigate,
          // close modal) before reading the explanation. Once chapterReady
          // flips true the hole becomes propagable.
          pointerEvents: chapterReady ? "none" : "auto",
          zIndex: 99996,
          transition,
        }}
      />
      {blockerRects.map((r, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          style={{
            position: "fixed",
            left: r.x,
            top: r.y,
            width: r.w,
            height: r.h,
            zIndex: 99997,
            pointerEvents: "auto",
          }}
        />
      ))}

      <div id="tutorialContainer" className="light">
        <div id="tutorialContent">
          <AmaryContent
            id={chapter.Amary.id}
            position={chapter.Amary.position}
            speed={chapter.Amary.speed}
            extraCSS={chapter.Amary.extraCSS}
            delayedCSS={chapter.Amary.delayedCSS}
            delay={chapter.Amary.delay}
          />
          <BoxContent
            position={chapter.ContentBox.position}
            animationSpeed={chapter.ContentBox.animationSpeed}
            text={chapter.ContentBox.text}
            speechSpeed={chapter.ContentBox.speechSpeed}
            speechFast={speechFast}
            onReady={handleBoxReady}
            extraCSS={chapter.ContentBox.extraCSS}
            delayedCSS={chapter.ContentBox.delayedCSS}
            delay={chapter.ContentBox.delay}
            continueTextPos={chapter.ContentBox.continueTextPos}
            triggerNextType={chapter.ContentBox.triggerNextType}
          />
        </div>
      </div>
    </>
  );
};
